import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import BackButton from "../../components/common/BackButton";
import {
  getCurrencySymbol,
  formatTaxPercent,
  formatAmount,
} from "../../helper/formatCurrency";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import {
  resolveProductPricing,
  expandVariantOptions,
} from "../../utils/productPricing";
import { getLocalizedName } from "../../utils/localizedText";
import { useAllocation } from "../../hooks/useAllocation";
import api from "../../utils/api";
import { useKitchenEta } from "../../hooks/useKitchenEta";
import KitchenEtaBadge from "../../components/Kitchen/KitchenEtaBadge";

// Smart search: typing "<code>+<qty>" or "<code>-<qty>" searches using just
// the code part, and (once that narrows to a single result) Enter adds or
// removes that many — e.g. "0011+3" adds 3 of product 0011, "0011-2"
// removes 2. A bare code with no +/- suffix still just adds 1 on Enter.
const parseSmartSearch = (value) => {
  const plusIndex = value.indexOf("+");
  const minusIndex = value.indexOf("-");
  const opIndex = plusIndex !== -1 ? plusIndex : minusIndex;

  if (opIndex === -1) return { code: value, op: null, qty: 1 };

  const code = value.substring(0, opIndex);
  const op = value.substring(opIndex, opIndex + 1);
  const qtyStr = value.substring(opIndex + 1);
  const parsedQty = parseInt(qtyStr, 10);

  return {
    code,
    op,
    qty: qtyStr && !isNaN(parsedQty) ? parsedQty : 1,
  };
};

// Whether an order line is for the picked search result - same product and,
// for a multi-variant product, the same variant (see expandVariantOptions).
const isSameOption = (line, product) =>
  line.productId === (product.productId || product._id) &&
  (line.variantUnit || "") === (product.variantUnit || "");

const OrderPage = () => {
  const { allocationId } = useParams();
  const kitchenEta = useKitchenEta({ allocationIds: [allocationId] }).tables[allocationId];
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurantId, locale, gstFraction, taxLabel } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);
  const gstPercent = gstFraction * 100;
  const { handleCancelAllocation } = useAllocation(restaurantId);

  const [customer, setCustomer] = useState(location.state?.customer || null);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [productResults, setProductResults] = useState([]);
  // Which result Up/Down is currently pointing at — this is a POS screen
  // driven mostly by keyboard, so arrow keys must be able to pick a result
  // without touching the mouse.
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [openSettleConfirm, setOpenSettleConfirm] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [addOnPickerProduct, setAddOnPickerProduct] = useState(null);
  // Map of addOnId -> chosen quantity (0/absent = not selected).
  const [pickerQuantities, setPickerQuantities] = useState({});
  const [editingAddOnsItemId, setEditingAddOnsItemId] = useState(null);
  const [loadingAddOnsFor, setLoadingAddOnsFor] = useState(null);

  // Hydrate customer/table info when arriving via direct URL/refresh
  // (no navigation state available in that case).
  useEffect(() => {
    if (customer || !allocationId) return;

    const loadAllocation = async () => {
      try {
        const res = await api.get(`/allocation/${allocationId}`);
        setCustomer({
          id: res.data.id,
          name: res.data.name,
          phone: res.data.phone,
          tableNumber: res.data.tableNumber,
          customerCount: res.data.customerCount,
        });
      } catch (err) {
        console.error("Failed loading allocation", err);
        toast.error("Could not load this order");
        navigate("/table-booking");
      }
    };

    loadAllocation();
  }, [customer, allocationId, navigate]);

  // Load existing order items
  useEffect(() => {
    if (!allocationId) return;
    let cancelled = false;

    const loadItems = async () => {
      setLoadingItems(true);
      try {
        const res = await api.get(`/orders/customerOrder/${allocationId}`);
        if (cancelled) return;

        // Use each line's own server _id as the local key — a product can
        // have more than one line (e.g. a delivered line plus a freshly
        // re-ordered one), so productId alone isn't unique here.
        const normalizedItems = (res.data.items || []).map((i) => ({
          id: i._id,
          lineId: i._id,
          name: i.productName,
          productId: i.productId,
          variantUnit: i.variantUnit || "",
          price: i.price,
          quantity: i.quantity,
          // Baseline to detect a local quantity bump that hasn't been sent
          // to the kitchen yet (see removeItem) - only ever set here, from
          // what the server actually has.
          savedQuantity: i.quantity,
          status: i.status || "queued",
          note: i.note || "",
          printedCount: i.printedCount || 0,
          isTaxable: i.isTaxable === true,
          isNew: false,
          // Without this, reopening the order page (every "Order to
          // Kitchen" navigates away, so this runs again on the next visit)
          // would leave addOns undefined on already-saved lines; saving
          // again after that would then send addOns: [] for them and wipe
          // out whatever was already picked.
          addOns: (i.addOns || []).map((a) => ({
            addOnId: a.addOnId,
            name: a.name,
            price: a.price,
            quantity: a.quantity,
          })),
        }));

        setItems(normalizedItems);
      } catch (err) {
        console.error("Failed loading order", err);
      } finally {
        if (!cancelled) setLoadingItems(false);
      }
    };

    loadItems();
    return () => {
      cancelled = true;
    };
  }, [allocationId]);

  const handleSearchProduct = async (value) => {
    setSearchText(value);

    // Search only by the code part — a "+3"/"-2" quantity suffix isn't part
    // of the product name/code and must never reach the search API.
    const { code } = parseSmartSearch(value);

    if (code.trim().length < 3) {
      setProductResults([]);
      return;
    }

    try {
      const res = await api.get(
        `/products/search?query=${encodeURIComponent(
          code,
        )}&restaurantId=${restaurantId}`,
      );
      // One result per variant for multi-variant products.
      setProductResults(expandVariantOptions(res.data));
      setHighlightedIndex(0);
    } catch (err) {
      console.error("Search failed:", err);
    }
  };

  // Add product to list
  const addItem = (product, chosenAddOns = [], qty = 1) => {
    // Only merge into a line that's still unsaved (added this session) -
    // bumping the quantity on an already-saved/kitchen line would hide the
    // new quantity inside "Previously Ordered" with no way to tell it
    // apart from what's already been sent. Re-ordering an already-saved
    // product (or a delivered one) always gets its own new line instead,
    // shown separately under "Recently Added".
    const existingNew = items.find(
      (i) => isSameOption(i, product) && i.isNew && i.status !== "delivered",
    );
    // For a variant option this is the chosen variant's price.
    const priceValue = resolveProductPricing(product).effectivePrice;
    const productId = product.productId || product._id;
    const addOnsTotal = chosenAddOns.reduce(
      (s, a) => s + Number(a.price || 0) * Number(a.quantity || 1),
      0,
    );

    if (existingNew) {
      setItems(
        items.map((i) =>
          i.id === existingNew.id ? { ...i, quantity: i.quantity + qty } : i,
        ),
      );
      toast.success(`${product.productName} quantity increased!`);
    } else {
      // A distinct id is only needed when some other line (old or new,
      // delivered or not) already exists for this product - otherwise
      // product._id is unique on its own.
      const hasOtherLine = items.some((i) => i.id === product._id);
      const lineId = hasOtherLine
        ? `${product._id}-${Date.now()}`
        : product._id;

      setItems([
        ...items,
        {
          id: lineId,
          // Variant options are already named "Product (Half)".
          name: product.productName,
          price: priceValue + addOnsTotal,
          productId,
          variantUnit: product.variantUnit || "",
          quantity: qty,
          note: "",
          status: "queued",
          isTaxable: product.isTaxable === true,
          isNew: true,
          addOns: chosenAddOns,
          // Cache the product's full add-on catalog so re-opening the
          // picker later (to change add-ons) doesn't need a re-fetch.
          availableAddOns: visibleAddOns(product),
        },
      ]);
      toast.success(`${product.productName} added to order!`);
    }
    setSearchText("");
    setProductResults([]);
  };

  const visibleAddOns = (product) =>
    (product?.addOns || []).filter((a) => a.isVisible !== false);

  // Arrow keys move the highlighted result; Enter applies the "+N"/"-N"
  // quantity typed into the box (see parseSmartSearch) against whichever
  // result is highlighted — plain Enter with no suffix just adds 1.
  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      if (!productResults?.length) return;
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, productResults.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      if (!productResults?.length) return;
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key !== "Enter" || !productResults?.length) return;
    e.preventDefault();

    const product = productResults[highlightedIndex] || productResults[0];
    const { op, qty } = parseSmartSearch(searchText);

    if (op === "-") {
      const existing = items.find(
        (i) => isSameOption(i, product) && i.status !== "delivered",
      );
      if (!existing) {
        toast.info(`${product.productName} isn't in the order`);
        return;
      }
      const remaining = existing.quantity - qty;
      if (remaining > 0) {
        setItems(
          items.map((i) =>
            i.id === existing.id ? { ...i, quantity: remaining } : i,
          ),
        );
        toast.success(`${product.productName} quantity decreased!`);
      } else {
        removeItem(existing.id);
      }
      setSearchText("");
      setProductResults([]);
      return;
    }

    handleProductPick(product, qty);
  };

  // Route a product search-result click through the add-on picker when it
  // has add-ons and isn't already in the order as a still-unsaved line
  // (that case just bumps that line's quantity - see addItem); otherwise
  // add it directly. An already-saved line for this product doesn't count
  // here: re-ordering it starts a fresh line of its own, which should get
  // its own add-on choice rather than silently inheriting none.
  const handleProductPick = (product, qty = 1) => {
    const existingNew = items.find(
      (i) => isSameOption(i, product) && i.isNew && i.status !== "delivered",
    );
    if (!existingNew && visibleAddOns(product).length > 0) {
      setEditingAddOnsItemId(null);
      setPickerQuantities({});
      setAddOnPickerProduct(product);
      // Close the search dropdown so it doesn't render on top of the picker.
      setSearchText("");
      setProductResults([]);
      return;
    }
    addItem(product, [], qty);
  };

  // Reopen the picker for an item already in the order, so add-ons can be
  // added/changed after the fact instead of only at the moment it's added.
  const openAddOnsEditor = async (row) => {
    setEditingAddOnsItemId(row.id);
    setPickerQuantities(
      Object.fromEntries(
        (row.addOns || []).map((a) => [a.addOnId, a.quantity || 1]),
      ),
    );

    if (row.availableAddOns) {
      setAddOnPickerProduct({ productName: row.name, addOns: row.availableAddOns });
      return;
    }

    setLoadingAddOnsFor(row.id);
    try {
      const res = await api.get(`/products/${row.productId}`);
      setAddOnPickerProduct({ productName: row.name, addOns: res.data?.addOns || [] });
    } catch (err) {
      console.error("Failed to load add-ons", err);
      toast.error("Failed to load add-ons for this product");
      setEditingAddOnsItemId(null);
    } finally {
      setLoadingAddOnsFor(null);
    }
  };

  const setPickerAddOnQty = (addOnId, qty) => {
    setPickerQuantities((prev) => {
      if (qty <= 0) {
        const next = { ...prev };
        delete next[addOnId];
        return next;
      }
      return { ...prev, [addOnId]: qty };
    });
  };

  const closeAddOnPicker = () => {
    setAddOnPickerProduct(null);
    setEditingAddOnsItemId(null);
  };

  const confirmAddOnPicker = () => {
    const chosen = visibleAddOns(addOnPickerProduct)
      .filter((a) => (pickerQuantities[a._id] || 0) > 0)
      .map((a) => ({
        addOnId: a._id,
        name: getLocalizedName(a.name),
        price: Number(a.price),
        quantity: pickerQuantities[a._id],
      }));

    if (editingAddOnsItemId) {
      setItems((prev) =>
        prev.map((i) => {
          if (i.id !== editingAddOnsItemId) return i;
          const oldAddOnsTotal = (i.addOns || []).reduce(
            (s, a) => s + Number(a.price || 0) * Number(a.quantity || 1),
            0,
          );
          const basePrice = (Number(i.price) || 0) - oldAddOnsTotal;
          const newAddOnsTotal = chosen.reduce(
            (s, a) => s + a.price * a.quantity,
            0,
          );
          return {
            ...i,
            addOns: chosen,
            price: basePrice + newAddOnsTotal,
            availableAddOns: addOnPickerProduct.addOns,
          };
        }),
      );
    } else {
      addItem(addOnPickerProduct, chosen);
    }
    closeAddOnPicker();
  };

  const incrementQty = (id) => {
    setItems(
      items.map((i) => (i.id === id ? { ...i, quantity: i.quantity + 1 } : i)),
    );
  };

  const decrementQty = (id) => {
    setItems(
      items
        .map((i) =>
          i.id === id && i.quantity > 1
            ? { ...i, quantity: i.quantity - 1 }
            : i,
        )
        .filter((i) => i.quantity > 0),
    );
  };

  const updateNote = (id, note) => {
    setItems(items.map((i) => (i.id === id ? { ...i, note } : i)));
  };

  const removeItem = async (id) => {
    const item = items.find((i) => i.id === id);

    // Items added in this session but never saved only exist in local
    // state - there's nothing to delete on the server yet, and calling
    // the API for them 404s (no CustomerOrder item to match against).
    if (item?.isNew) {
      setItems(items.filter((i) => i.id !== id));
      toast.info("Product removed from order");
      return;
    }

    // Re-adding an already-saved product bumps this line's quantity
    // locally (see addItem) without touching the server. Removing it at
    // that point must only undo that local bump - not delete the whole
    // line, which would also drop the quantity already sent to the
    // kitchen. A real delete only happens once there's no unsaved bump left.
    if (item.quantity > (item.savedQuantity ?? item.quantity)) {
      setItems(
        items.map((i) => (i.id === id ? { ...i, quantity: i.savedQuantity } : i)),
      );
      toast.info("Removed the extra quantity added this session");
      return;
    }

    try {
      await api.put(`/orders/customerOrder/${allocationId}/remove-item`, {
        productId: item.productId,
        lineId: item.lineId,
      });
      setItems(items.filter((i) => i.id !== id));
      toast.info("Product removed from order");
    } catch (err) {
      console.error("Remove failed", err);
      toast.error("Failed to remove item");
    }
  };

  const markDelivered = async (row) => {
    try {
      await api.put(`/orders/customerOrder/${allocationId}/deliver-item`, {
        productId: row.productId,
        lineId: row.lineId,
      });
      setItems(
        items.map((i) => (i.id === row.id ? { ...i, status: "delivered" } : i)),
      );
      toast.success(`${row.name} marked as delivered`);
    } catch (err) {
      console.error("Mark delivered failed", err);
      toast.error("Failed to mark item as delivered");
    }
  };

  const totalQuantity = items.reduce((sum, i) => sum + i.quantity, 0);
  const recentItems = items.filter((i) => i.isNew);
  const oldItems = items.filter((i) => !i.isNew);
  // New items only exist in local state until "Order to Kitchen" is
  // clicked - settling now would bill whatever was last saved on the
  // server and silently drop these, so block it until they're saved.
  const hasUnsavedItems = recentItems.length > 0;

  const subtotal = items.reduce(
    (sum, i) => sum + (Number(i.price) || 0) * i.quantity,
    0,
  );

  const taxableSubtotal = items.reduce((sum, i) => {
    if (i.isTaxable !== true) return sum;
    return sum + (Number(i.price) || 0) * i.quantity;
  }, 0);

  const gstAmount = taxableSubtotal * gstFraction;
  const grandTotal = subtotal + gstAmount;

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      const payload = {
        allocationId,
        tableNumber: customer?.tableNumber,
        restaurantId,
        username: localStorage.getItem("username") || "admin",
        items: items.map((i) => ({
          // Only previously-saved lines have a real server lineId; omit it
          // for lines added this session so the backend creates a new one
          // instead of merging into an unrelated line for the same product.
          lineId: i.isNew ? undefined : i.lineId,
          productId: i.productId,
          productName: i.name,
          variantUnit: i.variantUnit || "",
          price: i.price,
          quantity: i.quantity,
          note: i.note || "",
          addOns: i.addOns || [],
        })),
      };

      await api.post("/orders/customerOrder/save", payload);
      toast.success("Order Saved!");
      navigate("/table-booking");
    } catch (err) {
      console.error("Save order error:", err);
      toast.error(err.response?.data?.message || "Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmSettle = async () => {
    try {
      const payload = {
        allocationId,
        restaurantId,
        username: localStorage.getItem("username") || "guest",
        settledBy: localStorage.getItem("userId"),
      };

      await api.post("/orders/customerOrder/settle", payload);
      toast.success("Bill settled successfully!");
      setOpenSettleConfirm(false);
      navigate("/table-booking");
    } catch (err) {
      console.error("Settle bill error:", err);
      toast.error(err.response?.data?.message || "Failed to settle");
    }
  };

  const handleConfirmCancel = async () => {
    try {
      await handleCancelAllocation(allocationId);
      setOpenCancelConfirm(false);
      navigate("/table-booking");
    } catch (err) {
      console.error("Cancel allocation failed", err);
    }
  };

  const renderItemCard = (row) => {
    const isDelivered = row.status === "delivered";

    return (
    <div
      key={row.id}
      className="relative border border-gray-200 rounded-lg p-3 bg-white hover:border-gray-300 transition-colors"
    >
      {!isDelivered && (
        <button
          onClick={() => setItemToRemove(row)}
          disabled={saving}
          title="Remove item"
          className="absolute top-2 right-2 p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      )}

      <div className="flex items-start justify-between gap-2 pr-6">
        <div>
          <div className="font-medium text-sm text-gray-800">{row.name}</div>
          {row.addOns?.length > 0 && (
            <div className="text-xs text-gray-500">
              +{" "}
              {row.addOns
                .map((a) => (a.quantity > 1 ? `${a.name} x${a.quantity}` : a.name))
                .join(", ")}
            </div>
          )}
        </div>
        <span
          className={`shrink-0 inline-block px-2 py-0.5 rounded text-xs font-semibold ${row.status === "queued"
            ? "bg-yellow-200 text-yellow-800"
            : "bg-green-200 text-green-800"
            }`}
        >
          {row.status}
        </span>
      </div>

      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => decrementQty(row.id)}
            disabled={saving || isDelivered}
            className="p-1 border border-gray-200 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <strong className="text-sm min-w-[20px] text-center">{row.quantity}</strong>
          <button
            onClick={() => incrementQty(row.id)}
            disabled={saving || isDelivered}
            className="p-1 border border-gray-200 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
        <span className="text-sm font-semibold text-gray-700">
          {currencySymbol}
          {((Number(row.price) || 0) * row.quantity).toFixed(2)}
        </span>
      </div>

      <div className="flex items-center gap-2 mt-2">
        <input
          type="text"
          placeholder="Add a note (e.g. no onions)..."
          value={row.note}
          onChange={(e) => updateNote(row.id, e.target.value)}
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          disabled={saving || isDelivered || row.printedCount > 0}
        />
        <button
          type="button"
          onClick={() => openAddOnsEditor(row)}
          disabled={saving || isDelivered || loadingAddOnsFor === row.id}
          className="shrink-0 px-2 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 hover:bg-indigo-50 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loadingAddOnsFor === row.id
            ? "Loading..."
            : row.addOns?.length > 0
              ? "Edit add-ons"
              : "Add-ons"}
        </button>
      </div>

      {!isDelivered && !row.isNew && (
        <button
          type="button"
          onClick={() => markDelivered(row)}
          disabled={saving}
          className="w-full mt-2 px-2 py-1.5 text-xs font-semibold text-green-700 border border-green-300 bg-green-50 hover:bg-green-100 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Mark as Delivered
        </button>
      )}
    </div>
    );
  };

  return (
    <div className="p-3 max-w-2xl mx-auto">
      {/* Compact header */}
      <div className="flex items-center gap-3 mb-3">
        <BackButton to="/table-booking" />
        <div className="min-w-0">
          <h1 className="text-base font-bold text-gray-900 truncate">
            Order for {customer?.name || customer?.displayValue || "..."}
          </h1>
          {customer?.tableNumber && (
            <p className="text-xs text-gray-500">{customer.tableNumber}</p>
          )}
        </div>
        <KitchenEtaBadge eta={kitchenEta} className="ml-auto shrink-0" />
      </div>

      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        {/* Add More Items */}
        {showSearch ? (
          <div className="relative mb-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                autoFocus
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={searchText}
                onChange={(e) => handleSearchProduct(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                disabled={saving}
                autoComplete="off"
                placeholder="Search for products..."
              />
              <button
                onClick={() => {
                  setShowSearch(false);
                  setSearchText("");
                  setProductResults([]);
                }}
                title="Close search"
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {searchText && productResults?.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white max-h-[300px] overflow-y-auto z-[1000] shadow-lg rounded-lg border border-gray-200">
                <div className="py-1">
                  {productResults.map((p, index) => (
                    <button
                      key={p._id}
                      onClick={() => handleProductPick(p)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`w-full px-4 py-3 transition-colors text-left border-b border-gray-100 last:border-0 ${
                        index === highlightedIndex ? "bg-indigo-50" : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex justify-between items-center w-full">
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-800">
                            {p.productName}
                          </div>
                          {visibleAddOns(p).length > 0 && (
                            <div className="text-xs text-indigo-600 mt-0.5">
                              Has add-ons
                            </div>
                          )}
                        </div>
                        <strong className="text-sm text-gray-900 ml-3">
                          {currencySymbol}
                          {resolveProductPricing(p).effectivePrice}
                        </strong>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowSearch(true)}
            disabled={saving}
            className="w-full mb-3 px-3 py-2 border border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add More Items
          </button>
        )}

        {/* Order Items */}
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-medium text-gray-700">Order Items</label>
          {items.length > 0 && (
            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {items.length} {items.length === 1 ? "product" : "products"} · {totalQuantity} qty
            </span>
          )}
        </div>

        <div className="mb-3 space-y-3 max-h-[58vh] overflow-y-auto pr-1">
          {loadingItems ? (
            <div className="text-center text-sm text-gray-400 py-8">
              Loading order...
            </div>
          ) : items.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-8 border border-dashed border-gray-300 rounded-lg">
              No items added yet
            </div>
          ) : (
            <>
              {recentItems.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full bg-indigo-500" />
                    <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
                      Recently Added ({recentItems.length})
                    </span>
                  </div>
                  <div className="space-y-2">{recentItems.map(renderItemCard)}</div>
                </div>
              )}

              {oldItems.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full bg-gray-400" />
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Previously Ordered ({oldItems.length})
                    </span>
                  </div>
                  <div className="space-y-2">{oldItems.map(renderItemCard)}</div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Billing Summary + Action Buttons - frozen at the bottom of the page while content above scrolls */}
        <div className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-3 pb-4 sm:pb-6 bg-white border-t border-gray-200 shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.08)]">
          <div className="mb-3 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                Subtotal: {currencySymbol}
                {formatAmount(subtotal).toFixed(2)}
              </span>
              <span>
                {taxLabel} ({formatTaxPercent(gstPercent)}%): {currencySymbol}
                {formatAmount(gstAmount).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm font-bold text-gray-900 mt-1 pt-1 border-t border-gray-300">
              <span>Grand Total</span>
              <span>
                {currencySymbol}
                {formatAmount(grandTotal).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={handleSaveOrder}
              disabled={saving}
              className="px-2 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white text-sm font-medium leading-tight rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              Order to Kitchen
            </button>
            <button
              onClick={() => setOpenSettleConfirm(true)}
              disabled={saving || items.length === 0 || hasUnsavedItems}
              title={
                items.length === 0
                  ? "Add a product first"
                  : hasUnsavedItems
                    ? "Send the new items to the kitchen before settling"
                    : undefined
              }
              className="px-2 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white text-sm font-medium leading-tight rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              Settle Bill
            </button>
            <button
              onClick={() => setOpenCancelConfirm(true)}
              disabled={saving}
              className="px-2 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white text-sm font-medium leading-tight rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              Cancel Allocation
            </button>
          </div>
        </div>
      </div>

      {/* Settle Bill Confirmation Dialog */}
      {openSettleConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-[400px] mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Confirm Settle Bill</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">Are you sure you want to settle this bill?</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setOpenSettleConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSettle}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                Yes, Settle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      {openCancelConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-[400px] mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Cancel Allocation</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">Do you want to cancel this allocation?</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setOpenCancelConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                No
              </button>
              <button
                onClick={handleConfirmCancel}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Item Confirmation Dialog */}
      {itemToRemove && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-[400px] mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Remove Item</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Remove <span className="font-semibold">{itemToRemove.name}</span> from this order?
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setItemToRemove(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  removeItem(itemToRemove.id);
                  setItemToRemove(null);
                }}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add-on Picker */}
      {addOnPickerProduct && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-[400px] mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                {editingAddOnsItemId ? "Edit add-ons for" : "Add-ons for"}{" "}
                {addOnPickerProduct.productName}
              </h3>
            </div>
            <div className="px-6 py-4 max-h-[300px] overflow-y-auto space-y-1">
              {visibleAddOns(addOnPickerProduct).length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No add-ons available for this product.
                </p>
              ) : (
                visibleAddOns(addOnPickerProduct).map((addOn) => {
                  const qty = pickerQuantities[addOn._id] || 0;
                  return (
                    <div
                      key={addOn._id}
                      className="flex items-center justify-between py-2 border-b border-gray-100"
                    >
                      <div>
                        <div className="text-sm text-gray-800">{getLocalizedName(addOn.name)}</div>
                        <div className="text-xs text-gray-500">
                          {currencySymbol}
                          {Number(addOn.price).toFixed(2)} each
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setPickerAddOnQty(addOn._id, qty - 1)}
                          disabled={qty === 0}
                          className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                        </button>
                        <span className="text-sm font-semibold min-w-[16px] text-center">{qty}</span>
                        <button
                          type="button"
                          onClick={() => setPickerAddOnQty(addOn._id, qty + 1)}
                          className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-100"
                        >
                          <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={closeAddOnPicker}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddOnPicker}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg shadow-md transition-colors"
              >
                {editingAddOnsItemId ? "Save" : "Add Item"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderPage;
